import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Mail, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const Route = createFileRoute("/check-email")({
  head: () => ({
    meta: [
      { title: "Revisa tu correo — Brulo" },
      { name: "description", content: "Te enviamos un link de confirmación para activar tu cuenta de Brulo. Revisa tu bandeja de entrada o reenvía el correo." },
      { property: "og:title", content: "Revisa tu correo — Brulo" },
      { property: "og:description", content: "Confirma tu cuenta de Brulo desde el link que te enviamos por correo." },
      { name: "robots", content: "noindex" },
    ],
  }),
  validateSearch: (search: Record<string, unknown>) => ({
    email: (search.email as string) || "",
  }),
  component: CheckEmailPage,
});

function CheckEmailPage() {
  const { email } = useSearch({ from: "/check-email" });
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [sentOnce, setSentOnce] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const resend = async () => {
    if (!email) {
      toast.error("No tenemos tu email. Vuelve a iniciar sesión.");
      return;
    }
    if (cooldown > 0) return;
    setSending(true);
    const { error } = await supabase.auth.resend({
      type: "signup",
      email,
      options: { emailRedirectTo: window.location.origin + "/dashboard" },
    });
    setSending(false);
    if (error) {
      toast.error(error.message);
    } else {
      setSentOnce(true);
      setCooldown(60);
      toast.success("Correo reenviado");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 brulo-grain">
      <div className="w-full max-w-md text-center">
        <Link to="/" className="font-display text-3xl tracking-widest block mb-10">
          BRULO<span className="text-primary">.</span>
        </Link>
        <div className="bg-card border border-border rounded-lg p-8">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
            <Mail className="w-8 h-8 text-primary" />
          </div>
          <h1 className="font-display text-3xl mb-4">Revisa tu correo</h1>
          <p className="text-muted-foreground mb-6">
            Te enviamos un enlace de confirmación a{" "}
            <span className="text-foreground font-medium">{email || "tu correo"}</span>. Haz clic
            en el enlace para activar tu cuenta Brulo y entrar.
          </p>
          <button
            onClick={resend}
            disabled={sending || cooldown > 0}
            className="w-full py-3 rounded-md border border-border hover:border-primary hover:text-primary transition disabled:opacity-50 disabled:hover:border-border disabled:hover:text-foreground"
          >
            {sending
              ? "Enviando..."
              : cooldown > 0
                ? `Reenviar en ${cooldown}s`
                : "Reenviar correo de confirmación"}
          </button>
          {sentOnce && (
            <p className="mt-3 text-sm text-primary flex items-center justify-center gap-1.5">
              <Check className="w-4 h-4" /> Correo reenviado
            </p>
          )}
          <p className="text-xs text-muted-foreground mt-4">
            Revisa tu carpeta de spam si no lo ves en tu bandeja principal.
          </p>
          <Link
            to="/auth"
            className="inline-block text-sm text-muted-foreground hover:text-primary mt-6"
          >
            Volver a iniciar sesión
          </Link>
        </div>
      </div>
    </div>
  );
}
